import type { NodeKind, RunTheater } from "./run-types";
import type { VariantScaling } from "../mission/variants";

// The campaign layer above individual Operations (docs/design/GDD.md §The
// Campaign Layer, EVALUATION.md §Reconciliation 1): an Operation IS one
// theater deployment; this module tracks what happens to the WORLD between
// them. Pure rules — persistence lives in save.ts, sequencing in flow.
//
// Three mechanics:
//   Escalation — theaters you ignore heat up. Every finished Operation
//     raises REDFOR activity in every OTHER playable theater; clearing a
//     theater resets its own.
//   NG+ — each time a theater is cleared, its baseline pressure rises
//     permanently. Re-deployments fight a harder version.
//   Tasking unlocks — specialist mission types enter the node pool as
//     theaters fall (EVALUATION.md: "mission-type unlocks per theater
//     cleared"). DEFEND ships unlocked; DESTROY and AMBUSH are earned.

export type TheaterCampaignState = {
  escalation: number; // 0..ESCALATION_MAX, rises while ignored
  clears: number; // completed Operations here — drives NG+
};

export type CampaignState = {
  day: number; // campaign-date clock; one day per node fought
  theaters: Record<RunTheater, TheaterCampaignState>;
};

export const ESCALATION_MAX = 3;
// NG+ pressure caps so a farmed theater stays winnable
const NGP_CAP = 3;
// Per effective escalation step: enemy count and reinforcement pressure
const ESCALATION_COUNT_MULT = 0.12;
const ESCALATION_BUDGET = 2;

export const PLAYABLE_THEATERS: readonly RunTheater[] = ["stone-wall", "iron-market", "green-hammer", "iron-curtain", "arctic-wolf"];

export function isRunTheater(value: unknown): value is RunTheater {
  return PLAYABLE_THEATERS.includes(value as RunTheater);
}

export function defaultCampaign(): CampaignState {
  return {
    day: 0,
    theaters: {
      "stone-wall": { escalation: 0, clears: 0 },
      "iron-market": { escalation: 0, clears: 0 },
      "green-hammer": { escalation: 0, clears: 0 },
      "iron-curtain": { escalation: 0, clears: 0 },
      "arctic-wolf": { escalation: 0, clears: 0 },
    },
  };
}

// Advance the world after an Operation ends, however it ends. Victory
// pacifies the fought theater (escalation resets, clear counted); every
// other theater escalates regardless — time passed, REDFOR moved.
export function advanceCampaign(
  campaign: CampaignState,
  fought: RunTheater,
  outcome: "victory" | "defeat",
  nodesCleared: number,
): CampaignState {
  const theaters = { ...campaign.theaters };
  for (const key of PLAYABLE_THEATERS) {
    const t = theaters[key];
    theaters[key] = key === fought
      ? outcome === "victory"
        ? { escalation: 0, clears: t.clears + 1 }
        : { ...t }
      : { ...t, escalation: Math.min(ESCALATION_MAX, t.escalation + 1) };
  }
  return { day: campaign.day + Math.max(1, nodesCleared), theaters };
}

export function totalClears(campaign: CampaignState): number {
  return PLAYABLE_THEATERS.reduce((sum, key) => sum + campaign.theaters[key].clears, 0);
}

// The effective pressure a new Operation in this theater deploys into:
// current escalation plus the theater's permanent NG+ floor.
export function effectiveEscalation(campaign: CampaignState, theater: RunTheater): number {
  const t = campaign.theaters[theater];
  return t.escalation + Math.min(NGP_CAP, t.clears);
}

// Escalation folds into the per-node difficulty AFTER the column curve —
// same shape, hotter theater.
export function applyEscalation(scaling: VariantScaling, escalation: number): VariantScaling {
  if (escalation <= 0) return scaling;
  return {
    ...scaling,
    countMult: scaling.countMult * (1 + escalation * ESCALATION_COUNT_MULT),
    budgetDelta: scaling.budgetDelta + escalation * ESCALATION_BUDGET,
  };
}

// Tasking authorization: which node kinds the run map may generate.
// Structural kinds are always present; specialist taskings unlock as the
// campaign progresses so early profiles learn the escort loop first.
const ALWAYS_KINDS: readonly NodeKind[] = ["combat", "elite", "defend", "resupply", "final"];

export const KIND_UNLOCK_CLEARS: Partial<Record<NodeKind, number>> = {
  destroy: 1,
  ambush: 2,
  rescue: 2,
  delay: 3,
  sead: 3,
  extraction: 4,
};

// BLACKOUT (docs/design/MISSION_VARIETY.md): the mastery modifier. On a
// theater you have already cleared (NG+), night sorties fly with the
// thermal package DOWN — EO only, sensor locked. Only ever an upgrade of
// night, never sprung on a first-time theater.
export function sortieDarkness(night: boolean, theaterClears: number): "day" | "night" | "blackout" {
  if (!night) return "day";
  return theaterClears > 0 ? "blackout" : "night";
}

export function unlockedNodeKinds(clears: number): Set<NodeKind> {
  const kinds = new Set<NodeKind>(ALWAYS_KINDS);
  for (const [kind, needed] of Object.entries(KIND_UNLOCK_CLEARS) as [NodeKind, number][]) {
    if (clears >= needed) kinds.add(kind);
  }
  return kinds;
}

// ── Campaign map dossier (docs/design/THEME.md §The Five Theaters) ─────────
// All five theaters exist in the fiction; only some have maps yet. Locked
// entries render as INTEL PENDING cards — the world is bigger than the build.

export type TheaterDossier = {
  codename: string;
  region: string;
  profile: string; // one-line enemy/atmosphere read for the card
  theater?: RunTheater; // absent = not yet playable
};

export const CAMPAIGN_DOSSIER: readonly TheaterDossier[] = [
  {
    codename: "OPERATION STONE WALL",
    region: "AFGHANISTAN — HINDU KUSH",
    profile: "Soviet-equipped government remnants. AA on high ground. Pre-dawn dust.",
    theater: "stone-wall",
  },
  {
    codename: "OPERATION GREEN HAMMER",
    region: "CENTRAL AMERICA",
    profile: "Cuban-trained infantry under triple canopy. Ambush country.",
    theater: "green-hammer",
  },
  {
    codename: "OPERATION IRON MARKET",
    region: "MIDDLE EAST — CITY WITHHELD",
    profile: "Militia infantry, technicals, rooftop AA. Burning diesel at golden hour.",
    theater: "iron-market",
  },
  {
    codename: "OPERATION IRON CURTAIN",
    region: "EASTERN EUROPE — INNER GERMAN BORDER",
    profile: "Warsaw Pact armor and mobile AA across trench lines. Overcast, cold.",
    theater: "iron-curtain",
  },
  {
    codename: "OPERATION ARCTIC WOLF",
    region: "NORWAY — NORTHERN FLANK",
    profile: "Soviet arctic units and Spetsnaz in perpetual twilight.",
    theater: "arctic-wolf",
  },
];
